"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LabeledInput from "@/components/ui/LabeledInput";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import CaptchaField, {
  CaptchaFieldHandle,
} from "@/components/ui/CaptchaField";
import PasswordRequirements from "@/components/ui/PasswordRequirements";
import { getPasswordRequirementError, isStrongPassword } from "@/libs/password";

export type Lang = "en" | "fa";

const translations = {
  en: {
    title: "Reset password",
    subtitle: "Follow the steps to change your password",
    step1: {
      title: "Enter email",
      desc: "We will send a verification code to your email.",
      email: "Email",
      next: "Send code",
    },
    step2: {
      title: "Enter verification code",
      desc: "Enter the 6-digit code sent to your email.",
      otp: "Verification code",
      next: "Verify",
      back: "Back",
      resend: "Resend code",
      resendIn: (s: number) => `Resend in ${s}s`,
    },
    step3: {
      title: "New password",
      desc: "Choose a strong password.",
      password: "New password",
      confirm: "Confirm password",
      next: "Change password",
      back: "Back",
      rules: "Min 8 chars, upper, lower, number, symbol",
    },
    step4: {
      title: "Success",
      desc: "Your password has been changed.",
      done: "Done",
    },
    errors: {
      emailRequired: "Please enter a valid email.",
      otpRequired: "Please enter the 6-digit code.",
      otpInvalid: "Invalid or expired code.",
      passwordWeak: "Password does not meet requirements.",
      passwordMismatch: "Passwords do not match.",
      server: "An unexpected error occurred.",
      captchaRequired: "Please enter the captcha.",
    },
  },
  fa: {
    title: "بازیابی رمز عبور",
    subtitle: "برای تغییر رمز عبور مراحل را دنبال کنید",
    step1: {
      title: "ایمیل را وارد کنید",
      desc: "کد تایید به ایمیل شما ارسال می‌شود.",
      email: "ایمیل",
      next: "ارسال کد",
    },
    step2: {
      title: "کد تایید را وارد کنید",
      desc: "کد ۶ رقمی ارسال‌شده به ایمیل را وارد کنید.",
      otp: "کد تایید",
      next: "تایید",
      back: "بازگشت",
      resend: "ارسال مجدد کد",
      resendIn: (s: number) => `ارسال مجدد تا ${s}ثانیه`,
    },
    step3: {
      title: "رمز عبور جدید",
      desc: "یک رمز قوی انتخاب کنید.",
      password: "رمز عبور جدید",
      confirm: "تکرار رمز عبور",
      next: "تغییر رمز",
      back: "بازگشت",
      rules: "حداقل ۸ کاراکتر، حروف کوچک/بزرگ، عدد و نماد",
    },
    step4: {
      title: "موفقیت‌",
      desc: "رمز عبور شما با موفقیت تغییر کرد.",
      done: "تمام",
    },
    errors: {
      emailRequired: "لطفاً یک ایمیل معتبر وارد کنید.",
      otpRequired: "کد ۶ رقمی را وارد کنید.",
      otpInvalid: "کد نامعتبر یا منقضی شده است.",
      passwordWeak: "رمز عبور شرایط را ندارد.",
      passwordMismatch: "رمزها یکسان نیستند.",
      server: "خطایی رخ داد.",
      captchaRequired: "لطفا اسلان را تکمیل کنید.",
    },
  },
};

function validateEmail(email: string) {
  return /\S+@\S+\.\S+/.test(email);
}

function validateOtp(otp: string) {
  return /^\d{6}$/.test(otp);
}

export default function ForgotPasswordStepper({
  onClose,
  onPrefillEmail,
  defaultEmail = "",
  cooldownSeconds = 120,
  currentLang = "en",
}: {
  onClose: () => void;
  onPrefillEmail?: (email: string) => void;
  defaultEmail?: string;
  cooldownSeconds?: number;
  currentLang?: Lang;
}) {
  const t = translations[currentLang];

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState(defaultEmail);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const captchaRef = useRef<CaptchaFieldHandle>(null);
  const showCaptcha = true;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const sectionVariants = {
    collapsed: { height: 0, opacity: 0 },
    open: { height: "auto", opacity: 1 },
  };

  const handleRequestOtp = async () => {
    setError(null);
    setCaptchaError(null);
    if (!validateEmail(email)) {
      setError(t.errors.emailRequired);
      return;
    }
    if (showCaptcha && !captcha.trim()) {
      setCaptchaError(t.errors.captchaRequired);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, captcha: showCaptcha ? captcha : undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setFailedAttempts((prev) => prev + 1);
        if (data.error === "Captcha failed") {
          setCaptchaError("The captcha is incorrect or expired.");
        } else {
          setError(data.error || t.errors.server);
        }
        return;
      }
      setStep(1);
      setFailedAttempts(0);
      setResendCooldown(cooldownSeconds);
    } catch (e) {
      console.log(e);
      setFailedAttempts((prev) => prev + 1);
      setError(t.errors.server);
    } finally {
      setLoading(false);
      if (showCaptcha || failedAttempts + 1 >= 2) {
        captchaRef.current?.reset();
        setCaptcha("");
      }
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    await handleRequestOtp();
  };

  const handleVerifyOtp = async () => {
    setError(null);
    setCaptchaError(null);
    if (!validateOtp(otp)) {
      setError(t.errors.otpRequired);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t.errors.otpInvalid);
        return;
      }
      setStep(2);
    } catch (e) {
      console.log(e);
      setError(t.errors.server);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError(null);
    setCaptchaError(null);
    if (!isStrongPassword(password)) {
      setError(getPasswordRequirementError(password) ?? t.errors.passwordWeak);
      return;
    }
    if (password !== confirm) {
      setError(t.errors.passwordMismatch);
      return;
    }
    if (showCaptcha && !captcha.trim()) {
      setCaptchaError(t.errors.captchaRequired);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          otp,
          newPassword: password,
          captcha: showCaptcha ? captcha : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFailedAttempts((prev) => prev + 1);
        if (data.error === "Captcha failed") {
          setCaptchaError("The captcha is incorrect or expired.");
        } else {
          setError(data.error || t.errors.server);
        }
        return;
      }
      setStep(3);
      setFailedAttempts(0);
      onPrefillEmail?.(email);
      setPassword("");
      setConfirm("");
      setOtp("");
    } catch (e) {
      console.log(e);
      setFailedAttempts((prev) => prev + 1);
      setError(t.errors.server);
    } finally {
      setLoading(false);
      if (showCaptcha || failedAttempts + 1 >= 2) {
        captchaRef.current?.reset();
        setCaptcha("");
      }
    }
  };

  const StepHeader = useCallback(
    ({ title }: { title: string }) => (
      <div className="flex items-center">
        <h3 className="text-sm font-medium">{title}</h3>
      </div>
    ),
    []
  );

  return (
    <div
      className="px-4 w-full relative"
      dir={currentLang === "fa" ? "rtl" : "ltr"}
    >
      {/* Vertical line */}
      <div
        className={`absolute top-5 bottom-5 w-px bg-pinkCustom ${
          currentLang === "fa" ? "right-0" : "left-0"
        }`}
      />

      {/* Step 1: Email */}
      <div
        className={`relative border  rounded-md p-3 mb-3 bg-white ${
          currentLang === "fa" ? "pr-8" : "pl-8"
        } ${step === 0 ? "border-slateCustom" : "border-gray-300"}`}
      >
        {/* Step marker */}
        <div
          className={`absolute top-3 h-3 w-3 border-2 rounded-sm ${
            currentLang === "fa" ? "-right-[23px]" : "-left-[23px]"
          } ${
            step === 0
              ? "bg-white border-pinkCustom"
              : "bg-pinkCustom border-pinkCustom"
          }`}
        />
        <StepHeader title={t.step1.title} />
        <AnimatePresence initial={false}>
          {step === 0 && (
            <motion.div
              key="s1"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={sectionVariants}
              transition={{ duration: 0.2 }}
              className="overflow-hidden mt-3"
            >
              <p className="text-xs text-gray-500 mb-3">{t.step1.desc}</p>
              <LabeledInput
                label={t.step1.email}
                name="email"
                type="email"
                value={email}
                className="w-full bg-white mb-3"
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              <div className="mt-2 flex flex-col items-center gap-2">
                {showCaptcha && (
                  <CaptchaField
                    ref={captchaRef}
                    value={captcha}
                    onChange={setCaptcha}
                    onClearError={() => setCaptchaError(null)}
                    disabled={loading}
                    error={captchaError ?? undefined}
                  />
                )}
                {error && <p className="text-red-600 text-xs">{error}</p>}
              </div>
              <button
                onClick={handleRequestOtp}
                disabled={loading}
                className="bg-slateCustom w-full text-white font-semibold py-2 rounded disabled:opacity-60"
              >
                {loading ? (
                  <DotPulseLdr size={24} speed={1.3} color="white" />
                ) : (
                  t.step1.next
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Step 2: OTP */}
      <div
        className={`relative border  rounded-md p-3 mb-3 bg-white ${
          currentLang === "fa" ? "pr-8" : "pl-8"
        } ${step === 1 ? "border-slateCustom" : "border-gray-300"}`}
      >
        {/* Step marker */}
        <div
          className={`absolute top-3 h-3 w-3 border-2 rounded-sm ${
            currentLang === "fa" ? "-right-[23px]" : "-left-[23px]"
          } ${
            step === 1
              ? "bg-white border-pinkCustom"
              : "bg-pinkCustom border-pinkCustom"
          }`}
        />
        <StepHeader title={t.step2.title} />
        <AnimatePresence initial={false}>
          {step === 1 && (
            <motion.div
              key="s2"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={sectionVariants}
              transition={{ duration: 0.2 }}
              className="overflow-hidden mt-3"
            >
              <p className="text-xs text-gray-500 mb-3">{t.step2.desc}</p>
              <LabeledInput
                label={t.step2.otp}
                name="otp"
                type="text"
                value={otp}
                className="w-full bg-white mb-3"
                onChange={(e) => setOtp(e.target.value)}
                disabled={loading}
              />
              {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleVerifyOtp}
                  disabled={loading}
                  className="bg-slateCustom flex-1 text-white font-semibold py-2 rounded disabled:opacity-60"
                >
                  {loading ? (
                    <DotPulseLdr size={24} speed={1.3} color="white" />
                  ) : (
                    t.step2.next
                  )}
                </button>
              </div>
              <button
                onClick={handleResendOtp}
                disabled={loading || resendCooldown > 0}
                className="mt-2 text-slateCustom text-sm disabled:text-gray-400"
              >
                {resendCooldown > 0
                  ? t.step2.resendIn(resendCooldown)
                  : t.step2.resend}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Step 3: New Password */}
      <div
        className={`relative border  rounded-md p-3 mb-3 bg-white ${
          currentLang === "fa" ? "pr-8" : "pl-8"
        } ${step === 2 ? "border-slateCustom" : "border-gray-300"}`}
      >
        {/* Step marker */}
        <div
          className={`absolute top-3 h-3 w-3 border-2 rounded-sm ${
            currentLang === "fa" ? "-right-[23px]" : "-left-[23px]"
          } ${
            step === 2
              ? "bg-white border-pinkCustom"
              : "bg-pinkCustom border-pinkCustom"
          }`}
        />
        <StepHeader title={t.step3.title} />
        <AnimatePresence initial={false}>
          {step === 2 && (
            <motion.div
              key="s3"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={sectionVariants}
              transition={{ duration: 0.2 }}
              className="overflow-hidden mt-3"
            >
              <p className="text-xs text-gray-500 mb-3">{t.step3.desc}</p>
              <LabeledInput
                label={t.step3.password}
                name="password"
                type="password"
                value={password}
                className="w-full bg-white mb-3"
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <PasswordRequirements password={password} />
              <LabeledInput
                label={t.step3.confirm}
                name="confirm"
                type="password"
                value={confirm}
                className="w-full bg-white mb-3"
                onChange={(e) => setConfirm(e.target.value)}
                disabled={loading}
              />
              {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
              <div className="mb-2 flex flex-col items-center gap-2">
                {showCaptcha && (
                  <CaptchaField
                    ref={captchaRef}
                    value={captcha}
                    onChange={setCaptcha}
                    onClearError={() => setCaptchaError(null)}
                    disabled={loading}
                    error={captchaError ?? undefined}
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleResetPassword}
                  disabled={loading}
                  className="bg-slateCustom flex-1 text-white font-semibold py-2 rounded disabled:opacity-60"
                >
                  {loading ? (
                    <DotPulseLdr size={24} speed={1.3} color="white" />
                  ) : (
                    t.step3.next
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Step 4: Success */}
      <div
        className={`relative border rounded-md p-3 bg-white ${
          currentLang === "fa" ? "pr-8" : "pl-8"
        } ${step === 3 ? "border-slateCustom" : "border-gray-300"}`}
      >
        {/* Step marker */}
        <div
          className={`absolute bottom-3 h-3 w-3 border-2 rounded-sm ${
            currentLang === "fa" ? "-right-[23px]" : "-left-[23px]"
          } ${
            step === 3
              ? "bg-white border-pinkCustom"
              : "bg-pinkCustom border-pinkCustom"
          }`}
        />
        <StepHeader title={t.step4.title} />
        <AnimatePresence initial={false}>
          {step === 3 && (
            <motion.div
              key="s4"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={sectionVariants}
              transition={{ duration: 0.2 }}
              className="overflow-hidden mt-3"
            >
              <p className="text-xs text-gray-500 mb-3">{t.step4.desc}</p>
              <button
                onClick={onClose}
                className="bg-slateCustom w-full text-white font-semibold py-2 rounded"
              >
                {t.step4.done}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

